<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '../stores/auth';
import { Button, Input, Card, BrandLogo } from '../components/ui';

const router = useRouter();
const auth = useAuthStore();
const { t } = useI18n();

const email = ref('');
const password = ref('');
const loading = ref(false);
const errorMsg = ref('');

const brandName = import.meta.env.VITE_APP_BRAND_NAME || t('common.app.brand_fallback');

async function submit() {
  errorMsg.value = '';
  loading.value = true;
  try {
    await auth.login(email.value.trim().toLowerCase(), password.value);
    router.push({ name: 'home' });
  } catch (err: any) {
    errorMsg.value = err.response?.data?.message || t('errors.auth.bad_credentials');
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <div class="min-h-screen flex items-center justify-center px-4 bg-bg">
    <div class="w-full max-w-md flex flex-col gap-8">
      <div class="flex flex-col items-center gap-3">
        <BrandLogo :alt="brandName" size="lg" />
        <div class="text-center flex flex-col gap-1">
          <h1 class="text-2xl font-display font-semibold tracking-tight text-text">
            {{ brandName }}
          </h1>
          <p class="text-sm text-text-secondary">{{ t('auth.login.subtitle') }}</p>
        </div>
      </div>

      <Card variant="elevated" padding="lg">
        <form @submit.prevent="submit" class="flex flex-col gap-4">
          <Input
            v-model="email"
            type="email"
            :label="t('auth.login.email')"
            :placeholder="t('auth.login.email_placeholder')"
            autocomplete="email"
            required
            :disabled="loading"
          />
          <Input
            v-model="password"
            type="password"
            :label="t('auth.login.password')"
            :placeholder="t('auth.login.password_placeholder')"
            autocomplete="current-password"
            required
            :disabled="loading"
            :error="errorMsg"
          />
          <Button type="submit" variant="primary" size="lg" :loading="loading" full-width>
            {{ loading ? t('auth.login.loading') : t('auth.login.submit') }}
          </Button>
        </form>
      </Card>

      <p class="text-center text-xs text-text-muted">
        {{ t('common.app.footer') }}
      </p>
    </div>
  </div>
</template>
